import { useEffect, useMemo } from "react";
import {
  TextField,
  DatagridConfigurable,
  useTheme,
  NumberField,
  useTranslate,
} from "react-admin";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import AccountReceivableAgingExpandList from "../../components/accountReceivableAgings/expand/AccountReceivableAgingExpandList";
import accountReceivableAgingFilterList from "../../components/accountReceivableAgings/filter/accountReceivableAgingFilterList";
import AccountReceivableAgingAsideList from "../../components/accountReceivableAgings/aside/AccountReceivableAgingAsideList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";
import ManualReportList from "../../components/reports/ManualReportList";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const ExpandAll = () => {
  useEffect(() => {
    const expandAllBtn = document.querySelector(
      "table.RaDatagrid-table .RaDatagrid-thead .RaDatagrid-headerRow .RaDatagrid-expandHeader .RaDatagrid-expandIcon:not(.RaDatagrid-expanded)"
    );
    if (expandAllBtn) {
      var clickEvent = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: false,
      });
      expandAllBtn.dispatchEvent(clickEvent);
    }
  }, []);
  return null;
};

const AccountReceivableAgingList = () => {
  const { permissions, canSeeCompanyColumn, filterDefaultValues } =
    useCompanyScopedListFilters({
      start_date: firstDate,
      end_date: lastDate,
      cut_off: lastDate,
      kind: "per_surat_jalan",
    });
  const [theme] = useTheme("light");
  const t = useTranslate();

  const dayTrans = t("mine.label.day");

  const list = useMemo(
    () => (
      <ManualReportList
        storeKey={false} // Disabling Parameters Persistence
        title="mine.list_title.account_receivable_aging"
        empty={false}
        aside={<AccountReceivableAgingAsideList />}
        actions={<DownloadAction />}
        filters={accountReceivableAgingFilterList(permissions)}
        filterDefaultValues={filterDefaultValues}
        requiredFilters={["start_date", "end_date", "cut_off", "kind"]}
        queryOptions={{ meta: { sort: ["customers.code ASC"] } }} // overide default sort
        sx={{
          flexDirection: "column",
        }}
      >
        <DatagridConfigurable
          bulkActionButtons={false}
          // rowClick={rowClickHandler}
          // expandSingle={true}
          isRowExpandable={(row) =>
            !!row.customer_debts || !!row.sale_orders || !!row.sale_items
          } // sale_orders jika jenisnya per salesman, sale_items kalau per jenis
          expand={<AccountReceivableAgingExpandList />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          {canSeeCompanyColumn ? (
            <TextField source="company.alias" label="mine.label.depo" />
          ) : null}
          <TextField
            source="code"
            label="mine.label.code"
            emptyText="-"
            sx={{ width: "5%" }}
          />
          <TextField source="name" label="mine.label.name" />
          <ExpandAll />
          <NumberField source="total_ar" label="mine.label.customer_debt" />
          <NumberField source="total_overdue" label="Overdue" />
          <NumberField source="total_less_30" label={"< 30 " + dayTrans} />
          <NumberField
            source="total_between_30_60"
            label={"30 - 60 " + dayTrans}
          />
          <NumberField
            source="total_between_61_90"
            label={"61 - 90 " + dayTrans}
          />
          <NumberField
            source="total_between_91_120"
            label={"91 - 120 " + dayTrans}
          />
          <NumberField
            source="total_between_121_150"
            label={"121 - 150 " + dayTrans}
          />
          <NumberField source="total_above_150" label={"> 150 " + dayTrans} />
        </DatagridConfigurable>
      </ManualReportList>
    ),
    [theme, permissions, canSeeCompanyColumn, filterDefaultValues, dayTrans]
  );

  return list;
};

export default AccountReceivableAgingList;
